import { ListenerName } from '../../lib/listenerName';
import { Space, Tag, Tooltip, Typography } from 'antd';

/** An admin as the payout API names them (`approvedByAdmin`, `paidByAdmin`, …). */
export interface PayoutActor {
  id: string;
  name: string | null;
  email: string | null;
}

/** One payout row from `/admin/payouts/pending` or `/admin/payouts/history`. */
export interface AdminPayoutRow {
  id: string;
  listenerId: string;
  amountPaise: string;
  tdsPaise: string;
  netPayoutPaise: string;
  status: string;
  payoutMethod?: string | null;
  /** Bank: the account holder. UPI: the UPI holder's name (null on requests
   *  made before 2026-10-23 or from older app builds). */
  accountHolderName?: string | null;
  /** Bank payouts only; null on older requests. */
  bankName?: string | null;
  accountNumber?: string | null;
  ifsc?: string | null;
  upiId?: string | null;
  createdAt: string;
  approvedAt?: string | null;
  settledAt?: string | null;
  paymentReference?: string | null;
  reversalReason?: string | null;
  rejectedAt?: string | null;
  approvedByAdmin?: PayoutActor | null;
  paidByAdmin?: PayoutActor | null;
  rejectedByAdmin?: PayoutActor | null;
  listener: { id: string; displayName?: string | null; user: { handle: string } };
}

export function rupees(paise: string): string {
  return `₹${(Number(paise) / 100).toLocaleString('en-IN')}`;
}

export const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');

/** The payout's listener — Display Name, linked to their Earnings (the check before paying). */
export function ListenerCell({ row }: { row: AdminPayoutRow }) {
  return (
    <ListenerName
      listener={{ displayName: row.listener.displayName, handle: row.listener.user.handle, id: row.listener.id }}
      listenerId={row.listener.id}
      tab="earnings"
    />
  );
}

/** "Not provided" for a name an older app build never sent. */
const missing = <Typography.Text type="secondary">Not provided</Typography.Text>;

export function PayToCell({ row }: { row: AdminPayoutRow }) {
  return row.payoutMethod === 'upi' ? (
    <Space direction="vertical" size={0} data-testid={`payto-${row.id}`}>
      <span style={{ fontWeight: 600 }}>UPI · {row.accountHolderName ?? missing}</span>
      <span style={{ fontSize: 12 }}>{row.upiId}</span>
    </Space>
  ) : (
    <Space direction="vertical" size={0} data-testid={`payto-${row.id}`}>
      <span style={{ fontWeight: 600 }}>{row.accountHolderName ?? '—'}</span>
      <span style={{ fontSize: 12 }}>Bank: {row.bankName ?? missing}</span>
      <span style={{ fontSize: 11, color: '#8B8B8B' }}>
        A/C {row.accountNumber ?? '—'} · {row.ifsc ?? '—'}
      </span>
    </Space>
  );
}

/** "Anu Approver · 1/10/2026, 4:00 PM" — or "—" when nobody acted. */
export function ActorCell({
  actor,
  at,
  extra,
  testId,
}: {
  actor: PayoutActor | null | undefined;
  at: string | null | undefined;
  extra?: React.ReactNode;
  testId?: string;
}) {
  if (!actor && !at) return <Typography.Text type="secondary">—</Typography.Text>;
  return (
    <Space direction="vertical" size={0} data-testid={testId}>
      <Tooltip title={actor?.email ?? ''}>
        <span style={{ fontWeight: 600 }}>{actor ? (actor.name ?? 'Admin') : 'Unknown admin'}</span>
      </Tooltip>
      <span style={{ fontSize: 11, color: '#8B8B8B' }}>{when(at)}</span>
      {extra}
    </Space>
  );
}

export const STATUS_LABEL: Record<string, string> = {
  pending_approval: 'Pending review',
  approved: 'Approved · pay now',
  processing: 'Processing',
  settled: 'Paid',
  rejected: 'Rejected',
  reversed: 'Reversed',
};

export function StatusTag({ status }: { status: string }) {
  const color =
    status === 'settled' ? 'green' : status === 'approved' ? 'gold' : status === 'rejected' || status === 'reversed' ? 'red' : undefined;
  return <Tag color={color}>{STATUS_LABEL[status] ?? status}</Tag>;
}
